import { useState } from "react";
import type { ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import * as amplitude from "@amplitude/analytics-browser";

import {
  ArrowLeft,
  ArrowRight,
  Download,
  MagnifyingGlass
} from "@unified-checkout/design-system/icons/index";
import {
  Button,
  FileUploader,
  TableWrapper,
  Table,
  TableRow,
  TableHeaderCell,
  TableCell,
  Input,
  fileDownloader,
} from "@unified-checkout/design-system";
import type { TFileUploadInternalErrorType } from "libs/design-system/src/types/file_uploader/fileUploader";

import {
  BULK_PAYMENT_LINKS_EVENTS,
  BULK_PAYMENT_LINKS_EVENTS_PARAMS,
  BULK_PAYMENT_LINKS_EVENTS_VALUES
} from "@app/dashboard/constants/tracking_events/payment_links/bulk";
import { SHARED_EVENTS_PARAMS } from "@app/dashboard/constants/tracking_events/shared";

import type { TRowsToColumnsResult } from "@app/dashboard/types/innerLayout/excel-file-parser";
import { useLangStore } from "@app/dashboard/store/lang";
import { parseExcelFile } from "@app/dashboard/utils/getDataFromExcel";
import { deleteItemsFromObj } from "@app/dashboard/utils/deleteItemsFromObj";
import { bytesToKb } from "@app/dashboard/utils/fileSizeConverter";
import {
  AMOUNTS_FROM,
  BULK_INSTRUCTION_FILE_NAME,
  BULK_SHEET_FORMATS_ALLOWED,
  BULK_SHEET_TEMPLATE_FILE_NAME,
  FIELDS_IDS,
  BULK_PLS_SECTIONS_DIMENSIONS,
  NO_FILE,
} from "../../../utils";
import { ComponentError } from "@app/dashboard/components/shared/ComponentError";
import instructionsFileAr from "../../../files/bulk-payment-link-instructions-ar.pdf";
import instructionsFileEN from "../../../files/bulk-payment-link-instructions-en.pdf";
import bulkSheetTemplate from "../../../files/bulk-upload-payment-link-template.xlsx";
import type {
  ICustomersSheetSection
} from "@app/dashboard/types/innerLayout/payment_links/bulk/sections/customers-sheet";
import {
  BULK_SHEET_MAX_LENGTH,
  FIELDS_TO_DISPLAY_IN_DATATABLE,
  checkBulkSheetData,
  formatBulkData
} from "./utils";
import { SectionLoader } from "../../../Loader";
import styles from "./customers-sheet.module.scss";


const downloadTemplateFile = () => {

  amplitude.track(BULK_PAYMENT_LINKS_EVENTS.TEMPLATE_DOWNLOAD, {
    [BULK_PAYMENT_LINKS_EVENTS_PARAMS.TEMPLATE_FORMAT]: BULK_PAYMENT_LINKS_EVENTS_VALUES.XLSX
  });

  fileDownloader(bulkSheetTemplate, BULK_SHEET_TEMPLATE_FILE_NAME);
};

const downloadInstructionsFile = (lang: 'en' | 'ar') => {
  const file = lang === 'ar' ? instructionsFileAr : instructionsFileEN;
  fileDownloader(file, BULK_INSTRUCTION_FILE_NAME);
};


export const CustomersSheet = ({
  amountsFrom,
  fixedAmount,
  setFormValues,
  formErrors,
  setFormErrors,
  fullPageStatus
}: ICustomersSheetSection) => {
  const { lang } = useLangStore();
  const { t } = useTranslation();

  const [uploadedFile, setUploadedFile] = useState<File>();
  const [tableData, setTableData] = useState<Record<string, string[]>>({});
  const [filteredData, setFilteredData] = useState<Record<string, string[]>>({});

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    const term = e.target.value.trim().toLowerCase();

    if (!term) return setFilteredData(tableData);

    const keepRow = (rowIndex: number) => {
      return Object.values(tableData).some(col =>
        col[rowIndex]?.toLowerCase().startsWith(term)
      );
    };

    const filtered = Object.fromEntries(
      Object.entries(tableData).map(([col, arr]) => [
        col,
        arr.filter((_, i) => keepRow(i)),
      ])
    );

    setFilteredData(filtered);
  };

  const sheetUploadStartEventTracing = (file: File, fileType?: string) => {
    const fileSize = bytesToKb(file.size);

    amplitude.track(BULK_PAYMENT_LINKS_EVENTS.SHEET_UPLOAD_STARTED, {
      [BULK_PAYMENT_LINKS_EVENTS_PARAMS.FILE_TYPE]: fileType,
      [BULK_PAYMENT_LINKS_EVENTS_PARAMS.FILE_SIZE_KB]: fileSize
    });
  }

  const handleParseFile = async (file?: File) => {
    if (file) {

      const fileType = file.type === BULK_PAYMENT_LINKS_EVENTS_VALUES.TEXT_CSV ?
        BULK_PAYMENT_LINKS_EVENTS_VALUES.CSV
        :
        BULK_PAYMENT_LINKS_EVENTS_VALUES.XLSX;

      sheetUploadStartEventTracing(file, fileType);

      const results = (await parseExcelFile(file)) as TRowsToColumnsResult;

      if (results?.values) {
        const noOfRows = Math.max(...Object.values(results?.values).map((arr: any) => arr?.length || 0));
        const transformedData: Record<string, string[]> = {};

        FIELDS_TO_DISPLAY_IN_DATATABLE.forEach(key => {
          if (results.values[key]) {
            transformedData[key] = results.values[key].map(item => item.toString());
          }
        });

        setTableData(transformedData);
        setFilteredData(transformedData);
        setUploadedFile(file);

        const sheetResults = checkBulkSheetData({
          data: results.values,
          file,
          amountsFromSheet: amountsFrom === AMOUNTS_FROM.UPLOADED,
          fixedAmount: fixedAmount
        });

        const isSheetValid = sheetResults.isValid;
        const sheetError = sheetResults.error;

        if (isSheetValid) {

          amplitude.track(BULK_PAYMENT_LINKS_EVENTS.SHEET_UPLOAD_SUCCEEDED, {
            [BULK_PAYMENT_LINKS_EVENTS_PARAMS.ROW_COUNT]: noOfRows,
            [BULK_PAYMENT_LINKS_EVENTS_PARAMS.VALID_ROW_COUNT]: noOfRows,
            [BULK_PAYMENT_LINKS_EVENTS_PARAMS.INVALID_ROW_COUNT]: 0,
          });

          setFormValues(prev => ({ ...prev, bulkCustomers: formatBulkData(results.values) }));
          setFormErrors(prev => {
            const rest = deleteItemsFromObj(prev, FIELDS_IDS.BULK_SHEET);
            return rest;
          });

        } else {

          amplitude.track(BULK_PAYMENT_LINKS_EVENTS.SHEET_UPLOAD_FAILED, {
            [SHARED_EVENTS_PARAMS.ERR_DESCRIPTION]: sheetError,
          });

          sheetError && setFormErrors(prev => ({ ...prev, [FIELDS_IDS.BULK_SHEET]: sheetError }));
        }
      };

    } else {
      setTableData({});
      setFilteredData({});
      setFormValues(prev => deleteItemsFromObj(prev, 'bulkCustomers') as any);
      setFormErrors(prev => deleteItemsFromObj(prev, FIELDS_IDS.BULK_SHEET));
      setUploadedFile(undefined);
    }
  };

  const onFileUploaderInternalError = (errorType: TFileUploadInternalErrorType, message: string, file?: File) => {
    if (errorType !== NO_FILE) {
      const fileType = file?.type;

      sheetUploadStartEventTracing(file as File, fileType);

      amplitude.track(BULK_PAYMENT_LINKS_EVENTS.SHEET_UPLOAD_FAILED, {
        [SHARED_EVENTS_PARAMS.ERR_DESCRIPTION]: message,
      });
    }
  };

  const columns = Object.keys(filteredData);
  const noResults = !filteredData?.[columns[0]]?.length;
  const noOfRows = Math.max(...Object.values(filteredData).map((arr: any) => arr?.length || 0));
  const maxRowsReached = noOfRows > BULK_SHEET_MAX_LENGTH;

  return (
    fullPageStatus.loading ?
      <SectionLoader {...BULK_PLS_SECTIONS_DIMENSIONS.CUSTOMERS_SHEET} />
      :
      <div className={styles["customers-sheet"]}>

        <div className={styles["customers-sheet__header"]}>
          <div className={styles["customers-sheet__header__title"]}>
            {t('innerLayout.paymentLinksCreationPages.bulkPaymentLinks.sections.customersSheet.title')}
          </div>
          <Button
            size="sm"
            color="secondary"
            variant="link"
            text={t('innerLayout.paymentLinksCreationPages.bulkPaymentLinks.sections.customersSheet.learnToUse')}
            trailingIcon={lang === 'en' ? ArrowRight : ArrowLeft}
            onClickHandler={() => downloadInstructionsFile(lang)}
          />
        </div>

        <div className={styles["customers-sheet__file-uploader"]}>
          <FileUploader
            lang={lang}
            label={t('innerLayout.paymentLinksCreationPages.bulkPaymentLinks.sections.customersSheet.uploadSheet')}
            hintText={t('innerLayout.paymentLinksCreationPages.bulkPaymentLinks.sections.customersSheet.ensureValidData')}
            allowedSize={5}
            allowedFormats={BULK_SHEET_FORMATS_ALLOWED}
            onUploadHandler={(file) => handleParseFile(file as File | undefined)}
            error={formErrors?.[FIELDS_IDS.BULK_SHEET]}
            onError={onFileUploaderInternalError}
          />
        </div>

        {
          !!uploadedFile ?
            (
              !!columns.length && !maxRowsReached &&
              <div
                className={styles["customers-sheet__listing-table-wrapper"]}
                data-customers-listing-no-results={noResults}
              >

                <div className={styles["customers-sheet__listing-table-wrapper__search-bar"]}>
                  <Input
                    name="bulk-pl-listing-search"
                    placeholder={t('innerLayout.paymentLinksCreationPages.bulkPaymentLinks.sections.customersSheet.searchXCustomers', { number_of_customers: tableData[Object.keys(tableData)[0]]?.length || 0 })}
                    leadingIcon={MagnifyingGlass}
                    onChange={handleSearch}
                  />
                </div>

                <TableWrapper>
                  <Table maxHeight={'18.75rem'}>
                    <TableRow stickyOnOverflow={true}>
                      {
                        columns.map(columnName => (
                          <TableHeaderCell key={columnName} text={columnName} />
                        ))
                      }
                    </TableRow>
                    {
                      noResults ?
                        <div className={styles["customers-sheet__listing-table-wrapper__no-results"]}>
                          <ComponentError text={t('innerLayout.paymentLinksCreationPages.bulkPaymentLinks.sections.customersSheet.noResults')} />
                        </div>
                        :
                        filteredData[columns[0]]?.map((_, rowIndex) => (
                          <TableRow key={rowIndex}>
                            {
                              columns.map(columnName => (
                                <TableCell
                                  key={`${columnName}-${rowIndex}`}
                                  type="text"
                                  text={filteredData[columnName][rowIndex] || ''}
                                />
                              ))
                            }
                          </TableRow>
                        ))
                    }
                  </Table>
                </TableWrapper>
              </div>
              // :
              // <div className={styles["customers-sheet__listing-error-box"]}>
              //   <ComponentError text={t('innerLayout.paymentLinksCreationPages.bulkPaymentLinks.sections.customersSheet.noRecords')} />
              // </div>
            )
            :
            <Button
              size="sm"
              variant="link"
              text={t('innerLayout.paymentLinksCreationPages.bulkPaymentLinks.sections.customersSheet.downloadTemplate')}
              trailingIcon={Download}
              onClickHandler={() => downloadTemplateFile()}
            />
        }

      </div>
  )
}
